import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../Toast';
import { buildReadable, buildCsv } from './resultsExport';

// ─── Окно «Скопировать результаты» ───────────────────────────────────────────
//
// Два таба: «Текст» (читаемый, для ручного ввода) и «CSV» (под шаблон импорта).
// Текст в readOnly-textarea — выделяется и копируется вручную даже там, где
// navigator.clipboard заблокирован (Telegram WebView). Кнопка «Копировать всё»
// пробует clipboard API, при неудаче — select()+execCommand.
//
// Props:
//   tournament — объект турнира (players + rounds + eventTitle/eventDate/eventCreatedAt)
//   onClose    — колбэк закрытия

const TABS = [
  { key: 'readable', label: 'Текст' },
  { key: 'csv', label: 'CSV' },
];

const ResultsExportModal = ({ tournament, onClose }) => {
  const { addToast } = useToast();
  const [tab, setTab] = useState('readable');
  const [shown, setShown] = useState(false);
  const textareaRef = useRef(null);

  const readable = useMemo(() => buildReadable(tournament), [tournament]);
  const csv = useMemo(() => buildCsv(tournament), [tournament]);
  const text = tab === 'csv' ? csv : readable;

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Автовыделение текста при смене таба — сразу можно копировать вручную.
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.focus();
      el.select();
    }
  }, [tab]);

  const handleCopy = async () => {
    const el = textareaRef.current;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        addToast('Скопировано', 'success');
        return;
      }
      throw new Error('no clipboard');
    } catch {
      // Фолбэк для Telegram WebView / старых браузеров.
      if (el) {
        el.focus();
        el.select();
        try {
          const ok = document.execCommand('copy');
          addToast(ok ? 'Скопировано' : 'Выделите текст и скопируйте вручную',
            ok ? 'success' : 'info');
          return;
        } catch { /* ignore */ }
      }
      addToast('Выделите текст и скопируйте вручную', 'info');
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 400,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
          opacity: shown ? 1 : 0, transition: 'opacity 0.2s ease',
        }}
      />

      <div style={{
        position: 'relative', width: '100%', maxWidth: 480,
        background: '#fff', borderRadius: '20px 20px 0 0',
        padding: '18px 16px calc(16px + env(safe-area-inset-bottom))',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
        transform: shown ? 'translateY(0)' : 'translateY(20px)',
        opacity: shown ? 1 : 0,
        transition: 'opacity 0.2s ease, transform 0.2s ease',
        display: 'flex', flexDirection: 'column', gap: 12,
        maxHeight: '85vh',
      }}>
        {/* Заголовок */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
            Результаты
          </div>
          <button
            onClick={onClose}
            aria-label="Закрыть"
            style={{
              background: '#f3f4f6', border: 'none', borderRadius: 8,
              width: 30, height: 30, cursor: 'pointer', color: '#6b7280',
              fontSize: 18, lineHeight: 1,
            }}
          >×</button>
        </div>

        {/* Табы */}
        <div style={{ display: 'flex', gap: 6 }}>
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                flex: 1, padding: '8px 10px', borderRadius: 9,
                border: tab === t.key ? '1.5px solid #d97706' : '1.5px solid #e5e7eb',
                background: tab === t.key ? '#fffbeb' : '#fff',
                color: tab === t.key ? '#b45309' : '#6b7280',
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Текст */}
        <textarea
          ref={textareaRef}
          readOnly
          value={text}
          onFocus={(e) => e.target.select()}
          style={{
            width: '100%', flex: 1, minHeight: 180, maxHeight: '45vh',
            resize: 'none', boxSizing: 'border-box',
            padding: '10px 12px', borderRadius: 12,
            border: '1.5px solid #e5e7eb', background: '#f9fafb',
            fontSize: 12.5, lineHeight: 1.5, color: '#111827',
            fontFamily: tab === 'csv'
              ? 'ui-monospace, SFMono-Regular, Menlo, monospace'
              : 'inherit',
            whiteSpace: 'pre', overflow: 'auto',
          }}
        />

        {/* Копировать */}
        <button
          onClick={handleCopy}
          style={{
            width: '100%', padding: '13px', borderRadius: 12, border: 'none',
            background: 'linear-gradient(135deg, #d97706, #b45309)',
            color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Копировать всё
        </button>
      </div>
    </div>
  );
};

export default ResultsExportModal;
